/**
 * Типы объекта и подобъекта — справочник с сервера (objects/types).
 *
 * Раньше список лежал на фронте хардкодом (TypesObject: 15 типов объекта, а у справки к импорту
 * объектов — ещё и свой набор кодов в переводах), и типы, заведённые на бэкенде позже, в выпадашки
 * не попадали вовсе. Название сервер отдаёт уже на языке пользователя, а смена языка перезагружает
 * страницу, поэтому справочник загружает cloud.guard один раз при входе в облако.
 */

export interface ObjectType {
    id: number;
    /** название на языке пользователя — им и подписываются выпадашки */
    name: string;
    /** сокращение (МКД, ТЦ), есть не у всех типов */
    abbr?: string | null;
    /** у типа подобъекта: под объектами каких типов он допустим; null или пусто — под любым */
    parent_type_ids?: number[] | null;
}

export interface ObjectTypesResponse {
    object_types?: ObjectType[];
    inner_object_types?: ObjectType[];
}

let objectTypesList: ObjectType[] = [];
let innerObjectTypesList: ObjectType[] = [];

export function setObjectTypes(types: ObjectTypesResponse | null | undefined): void {
    objectTypesList = types?.object_types?.length ? [...types.object_types] : [];
    innerObjectTypesList = types?.inner_object_types?.length ? [...types.inner_object_types] : [];
}

/** Типы объекта. */
export function objectTypes(): ObjectType[] {
    return objectTypesList;
}

/** Типы подобъекта. */
export function innerObjectTypes(): ObjectType[] {
    return innerObjectTypesList;
}

/**
 * Типы подобъекта, допустимые под объектом данного типа: сервер перечисляет родителей
 * в parent_type_ids. Списка нет или он пуст — тип годится под любой объект (так отвечает
 * и бэкенд, который поля ещё не отдаёт).
 *
 * Тип родителя не заполнен (null, 0, пустая строка) или родитель ещё не загружен (undefined) —
 * сужать не по чему, годится любой тип подобъекта.
 * Уже выбранный тип оставляем, даже если родителю он не подходит: иначе поле молча показало бы
 * пустоту вместо сохранённого значения.
 */
export function innerObjectTypesFor(parentTypeId: number | null | undefined, selectedId?: number | null): ObjectType[] {
    if (!parentTypeId) {
        return innerObjectTypesList;
    }
    // сравниваем числами: id родителя в дереве — число, а в parent_type_ids они могут прийти строками
    const parent = Number(parentTypeId);
    return innerObjectTypesList.filter(
        (type) => !type.parent_type_ids?.length || type.parent_type_ids.some((id) => Number(id) === parent) || type.id === selectedId
    );
}

/** Запись справочника по id: у подобъекта свой список, поэтому нужен и тип узла. */
export function objectType(nodeType: string | null | undefined, typeId: number | null | undefined): ObjectType | null {
    const types = nodeType === 'innerObject' ? innerObjectTypesList : objectTypesList;
    return types.find((type) => type.id === typeId) ?? null;
}

/**
 * Типы, у которых в src/assets/icons/map лежит свой значок (type-<id>.svg). Списком, а не
 * «id не больше N»: значки рисуются вручную и по именам типов, а нумерация справочника сплошная —
 * у типа, заведённого на бэкенде позже, своего рисунка нет, и файла с таким номером не существует.
 * Такому типу и на карте, и в списках достаётся нейтральный значок (type-unknown.svg).
 */
const TYPES_WITH_GLYPH = new Set([
    // типы объекта
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
    32, 33, 34, 35, 36, 37, 38, 39, 40, 41,
    // типы подобъекта
    102, 105, 106, 107, 108, 109, 110, 111, 112, 113
]);

/** Есть ли у типа свой значок. */
function hasTypeGlyph(typeId: number | null | undefined): boolean {
    return typeId != null && TYPES_WITH_GLYPH.has(Number(typeId));
}

/**
 * Значок типа — один и тот же файл для карты и для списков: на булавке он ложится белым поверх
 * цветной капли, в списке и в дереве перекрашивается в currentColor. Рисунки обведены potrace
 * с прежних PNG-булавок, поэтому на карте всё выглядит как раньше.
 *
 * Нет своего значка — null: в списке и в дереве у такого типа остаётся прежняя иконка, а карта
 * подставит нейтральный значок — маркер без рисунка выглядел бы недорисованным.
 */
export function objectTypeGlyphSrc(typeId: number | null | undefined): string | null {
    return hasTypeGlyph(typeId) ? `assets/icons/map/type-${Number(typeId)}.svg` : null;
}
